/**
 * Trovva — CRM v2
 *
 * Supabase-wired pipeline with:
 *   - List ↔ Kanban view toggle (remembered per-user via localStorage)
 *   - Lead cards with score chip, stale badge, tags, quick actions
 *   - Bulk multi-select with mass-actions bar (assign / tag / delete)
 *   - Click a card → LeadDetailDrawer (timeline / tasks / notes)
 *   - Tag + status + type + search filters
 *   - Demo-mode fallback to canned data (existing mock-data prop)
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Plus, Search, Upload, Download, Users, Kanban as KanbanIcon, List as ListIcon,
  Phone, MessageSquare, Mail, ChevronRight, Star, Trash2, Edit2,
  TagIcon as _Tag, Filter, RefreshCw, Loader2, X, CheckSquare, Square,
  Bookmark, BookmarkCheck, ChevronDown, MoreHorizontal,
  Clock, Check, Pause, XCircle, ArrowRight, Inbox,
  UserPlus, AlertCircle, CheckCircle2,
} from 'lucide-react';
import { theme } from '../../lib/theme';
import { supabase } from '../../lib/supabase';
import { computeLeadScore, stalenessOf, daysSinceContact } from '../../lib/leadScoring';
import { isFeatureVisible } from '../../lib/permissions';
import { COMMERCIAL_CRM_PIPELINE, COMMERCIAL_SUB_TYPES } from '../../lib/constants';
import EmptyState from '../common/EmptyState';
import LoadingSpinner from '../common/LoadingSpinner';
import AddLeadForm from './AddLeadForm';
import CsvImport from './CsvImport';
// AskAI removed — Brain Chat is the single AI entry point
import LeadDetailDrawer from './LeadDetailDrawer';
import ContactDetailView from './ContactDetailView';
import StageGateModal from './StageGateModal';
import ActionPlansTab from './ActionPlansTab';
import LeadRoutingTab from './LeadRoutingTab';
import AutomationsTab from './AutomationsTab';
import TasksTab from './TasksTab';
import CalendarView from '../calendar/CalendarView';
import { missingForStage } from '../../lib/stageGating';
import GuidedTour from '../common/GuidedTour';
import AgentFilterCombo from '../common/AgentFilterCombo';
import { CRM_TAB_TOURS } from '../../lib/tourSteps';

// Sales funnel only — once a lead reaches "offer_accepted" it converts
// to a Deal on the Transactions board (post-contract pipeline lives there).
const PIPELINE = [
  { key: 'new',              label: 'New',              color: '#6366F1' },   // Indigo
  { key: 'contacted',        label: 'Contacted',        color: '#F59E0B' },   // Amber
  { key: 'qualified',        label: 'Qualified',        color: '#0EA5E9',  aliases: ['nurturing'] },  // Sky
  { key: 'active_search',    label: 'Active search',    color: '#2563EB' },   // Bold blue
  { key: 'offer_submitted',  label: 'Offer submitted',  color: '#8B5CF6' },   // Violet
  { key: 'offer_accepted',   label: 'Offer accepted',   color: '#16A34A' },   // Bold green
];

const VIEW_MODE_KEY = 'trovva_leads_view_mode';

export default function LeadsView({ leads: legacyLeads, onAddLead, demoMode, showToast, org, initialTab, onTabConsumed, userRole }) {
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem(VIEW_MODE_KEY) || 'kanban'; } catch { return 'kanban'; }
  });
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [openLeadId, setOpenLeadId] = useState(null);
  const [openInboxItemId, setOpenInboxItemId] = useState(null); // when opened from Inbox
  const [q, setQ] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [commercialSubFilter, setCommercialSubFilter] = useState(''); // buyer_rep, seller_rep, tenant_rep, landlord_rep
  const [crmTab, setCrmTab] = useState('inbox');
  const [automationSection, setAutomationSection] = useState('segments'); // segments | action_plans | routing | automations
  const [selected, setSelected] = useState(new Set());
  // ── Stage gate: when a drag/drop or status-change needs extra fields ──
  const [stageGate, setStageGate] = useState(null); // { lead, targetStatus }
  // ── Saved views ──
  const [savedViews, setSavedViews] = useState([]);
  const [activeSavedViewId, setActiveSavedViewId] = useState(null);
  // ── Smart lists (horizontal tabs) ──
  const [smartLists, setSmartLists] = useState([]);
  const [smartListsLoading, setSmartListsLoading] = useState(true);
  const [activeSmartListId, setActiveSmartListId] = useState(null);
  const [smartListContacts, setSmartListContacts] = useState([]);
  const [smartListLoading, setSmartListLoading] = useState(false);
  const [showSmartListEditor, setShowSmartListEditor] = useState(false);
  const [editingSmartList, setEditingSmartList] = useState(null);
  const [agents, setAgents] = useState([]);
  // ── Board scoping: broker can filter My Leads / All / specific agent ──
  const isBrokerOrAdmin = userRole === 'broker' || userRole === 'admin';
  const [boardAgent, setBoardAgent] = useState('mine'); // 'mine' | 'all' | agent UUID

  const setViewModePersisted = (v) => {
    setViewMode(v);
    try { localStorage.setItem(VIEW_MODE_KEY, v); } catch { /* noop */ }
  };

  const load = useCallback(async () => {
    setError(null);
    if (demoMode) {
      // Fall back to the legacy mock leads shape, mapped to v2 schema
      const mapped = (legacyLeads || []).map((l, i) => ({
        id: `demo-${i}`,
        first_name: (l.name || '').split(' ')[0] || '',
        last_name:  (l.name || '').split(' ').slice(1).join(' ') || '',
        email: l.email || null, phone: l.phone || null,
        city: l.city || null, state: l.state || null,
        lead_status: (l.status || 'new').toLowerCase().replace(/\s+/g, '_'),
        lead_type:   (l.type || 'buyer').toLowerCase(),
        lead_source: l.source || null,
        budget: l.budget || null, timeline: l.timeline || null,
        tags: Array.isArray(l.tags) ? l.tags : [],
        notes: l.notes || null,
        last_contacted: l.lastActivity || new Date().toISOString(),
        updated_at: l.lastActivity || new Date().toISOString(),
        created_at: new Date().toISOString(),
        pinned: false,
      }));
      setLeads(mapped);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not signed in');
      let url = '/api/leads?limit=300';
      if (isBrokerOrAdmin && boardAgent === 'mine') url += '&agent_id=me';
      else if (isBrokerOrAdmin && boardAgent && boardAgent !== 'all') url += `&agent_id=${boardAgent}`;
      // boardAgent === 'all' → all leads (no filter)
      const r = await fetch(url, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || `Failed (${r.status})`);
      setLeads(data.leads || []);
    } catch (e) {
      setError(e.message);
    } finally { setLoading(false); }
  }, [demoMode, legacyLeads, isBrokerOrAdmin, boardAgent]);

  useEffect(() => { load(); }, [load]);

  // ── Load saved views on mount (skip in demo mode) ──
  useEffect(() => {
    if (demoMode) return;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;
        const r = await fetch('/api/lead-saved-views', {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (r.ok) {
          const data = await r.json();
          setSavedViews(data.views || []);
          // Auto-apply default view on first mount
          const def = (data.views || []).find(v => v.is_default);
          if (def) {
            applyView(def);
            setActiveSavedViewId(def.id);
          }
        }
      } catch { /* noop */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demoMode]);

  const applyView = (v) => {
    if (!v?.filter) return;
    const f = v.filter;
    setQ(f.q || '');
    setFilterType(f.filterType || '');
    setFilterStatus(f.filterStatus || '');
    setFilterTag(f.filterTag || '');
    if (v.view_mode) setViewModePersisted(v.view_mode);
    setActiveSavedViewId(v.id);
  };

  // ── Load org agents (skip in demo mode) ──
  useEffect(() => {
    if (demoMode) return;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;
        const r = await fetch('/api/team', { headers: { Authorization: `Bearer ${session.access_token}` } });
        if (r.ok) {
          const data = await r.json();
          setAgents((data.team || data.members || []).map(u => ({ id: u.id, full_name: u.full_name || u.name })));
        }
      } catch { /* noop */ }
    })();
  }, [demoMode]);

  // ── Fetch smart lists for the horizontal tab bar ──
  const loadSmartLists = useCallback(async () => {
    if (demoMode) { setSmartListsLoading(false); return; }
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const r = await fetch('/api/smart-lists', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const data = await r.json();
      if (r.ok) setSmartLists(data.smart_lists || []);
    } catch { /* noop */ }
    finally { setSmartListsLoading(false); }
  }, [demoMode]);

  useEffect(() => { loadSmartLists(); }, [loadSmartLists]);

  // ── Fetch contacts for a selected smart list ──
  const loadSmartListContacts = useCallback(async (listId) => {
    if (!listId || demoMode) return;
    setSmartListLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not signed in');
      const r = await fetch(`/api/smart-lists?id=${listId}&contacts=1&limit=200`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Failed to load segment');
      setSmartListContacts(data.contacts || []);
    } catch (e) {
      showToast?.(e.message);
      setSmartListContacts([]);
    } finally { setSmartListLoading(false); }
  }, [demoMode, showToast]);

  const handleSelectSmartList = useCallback((listId) => {
    if (listId === activeSmartListId) { return; }
    setActiveSmartListId(listId);
    if (listId) loadSmartListContacts(listId);
    else setSmartListContacts([]);
  }, [activeSmartListId, loadSmartListContacts]);

  const handleClearSmartList = useCallback(() => {
    setActiveSmartListId(null);
    setSmartListContacts([]);
  }, []);

  const handleSaveSmartList = async (payload) => {
    if (demoMode) return;
    const isEdit = !!editingSmartList;
    const url = isEdit ? `/api/smart-lists?id=${editingSmartList.id}` : '/api/smart-lists';
    const method = isEdit ? 'PATCH' : 'POST';
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (r.ok) {
        await loadSmartLists();
        setShowSmartListEditor(false);
        setEditingSmartList(null);
        showToast?.(isEdit ? 'Segment updated' : 'Segment created');
      }
    } catch (e) { showToast?.(e.message); }
  };

  const handleDeleteSmartList = async (id) => {
    if (!window.confirm('Delete this segment?')) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      await fetch(`/api/smart-lists?id=${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (activeSmartListId === id) handleClearSmartList();
      await loadSmartLists();
    } catch (e) { showToast?.(e.message); }
  };

  const saveCurrentAsView = async (name, isDefault = false) => {
    if (demoMode) { showToast?.('Saved views require a real account.'); return; }
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch('/api/lead-saved-views', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          filter: { q, filterType, filterStatus, filterTag },
          view_mode: viewMode,
          is_default: isDefault,
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Save failed');
      setSavedViews(vs => [data.view, ...vs.filter(v => !data.view.is_default || !v.is_default)]);
      setActiveSavedViewId(data.view.id);
      showToast?.('View saved');
    } catch (e) { showToast?.(e.message); }
  };

  const deleteSavedView = async (id) => {
    if (demoMode) return;
    if (!window.confirm('Delete this view?')) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      await fetch(`/api/lead-saved-views?id=${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      setSavedViews(vs => vs.filter(v => v.id !== id));
      if (activeSavedViewId === id) setActiveSavedViewId(null);
    } catch (e) { showToast?.(e.message); }
  };

  // Enhance each lead with computed score + staleness (client-side)
  const enriched = useMemo(() => {
    return leads.map(l => ({
      ...l,
      _score: computeLeadScore(l),
      _staleness: stalenessOf(l),
      _days_since: daysSinceContact(l),
    }));
  }, [leads]);

  // Apply filters
  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return enriched.filter(l => {
      // Board-level filter: Residential board excludes commercial, Commercial board shows only commercial
      if (crmTab === 'people' && l.lead_type === 'commercial') return false;
      if (crmTab === 'commercial' && l.lead_type !== 'commercial') return false;
      // Commercial sub-type filter (buyer_rep, seller_rep, tenant_rep, landlord_rep)
      if (crmTab === 'commercial' && commercialSubFilter && l.commercial_sub_type !== commercialSubFilter) return false;
      if (filterType && l.lead_type !== filterType) return false;
      if (filterStatus && l.lead_status !== filterStatus) return false;
      if (filterTag && !(l.tags || []).includes(filterTag)) return false;
      if (query) {
        const hay = [l.first_name, l.last_name, l.company_name, l.email, l.phone, l.city, l.lead_source]
          .filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(query)) return false;
      }
      return true;
    });
  }, [enriched, q, filterType, filterStatus, filterTag, crmTab, commercialSubFilter]);

  // All unique tags across visible leads (for the tag filter dropdown)
  const allTags = useMemo(() => {
    const s = new Set();
    for (const l of enriched) for (const t of (l.tags || [])) s.add(t);
    return Array.from(s).sort();
  }, [enriched]);

  // Active pipeline based on which board tab is selected
  const activePipeline = crmTab === 'commercial' ? COMMERCIAL_CRM_PIPELINE : PIPELINE;

  // Bucket for kanban (with alias mapping for backward compat)
  const byStatus = useMemo(() => {
    const m = {};
    const aliasMap = {}; // e.g. { nurturing: 'qualified' }
    for (const p of activePipeline) {
      m[p.key] = [];
      if (p.aliases) for (const a of p.aliases) aliasMap[a] = p.key;
    }
    for (const l of filtered) {
      const bucket = m[l.lead_status] ? l.lead_status : (aliasMap[l.lead_status] || null);
      if (bucket && m[bucket]) m[bucket].push(l);
    }
    return m;
  }, [filtered, activePipeline]);

  const toggleSelect = (id) => setSelected(s => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const clearSelection = () => setSelected(new Set());

  const handleLeadSaved = async (leadData) => {
    if (demoMode) {
      await onAddLead?.(leadData);
      return;
    }
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not signed in');
      const r = await fetch('/api/leads', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(leadData),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Save failed');
      showToast?.('Lead saved');
      load();
    } catch (e) { showToast?.(e.message); }
  };

  // Drag-drop on kanban — gate on required fields for certain stages
  const handleDrop = async (lead, toStatus) => {
    if (lead.lead_status === toStatus) return;
    // If target stage has requirements the lead doesn't meet, open the gate modal
    const missing = missingForStage(lead, toStatus);
    if (missing.length > 0) {
      setStageGate({ lead, targetStatus: toStatus });
      return;
    }
    await commitStageChange(lead.id, { lead_status: toStatus });
  };

  const commitStageChange = async (leadId, patch) => {
    // Extract deal creation signal before sending to API
    const shouldCreateDeal = patch._create_deal;
    const dealData = patch._deal_data;
    const cleanPatch = { ...patch };
    delete cleanPatch._create_deal;
    delete cleanPatch._deal_data;

    // Optimistic update
    setLeads(ls => ls.map(l => l.id === leadId ? { ...l, ...cleanPatch } : l));
    if (demoMode) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch(`/api/leads?id=${leadId}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(cleanPatch),
      });
      if (!r.ok) throw new Error((await r.json()).error || 'Failed');

      // Auto-generate offer document from template when offer is submitted
      if (cleanPatch.lead_status === 'offer_submitted' && cleanPatch.property_address) {
        try {
          // Find the org's offer template
          const tplRes = await fetch('/api/pdf-autofill?action=list_templates', {
            headers: { Authorization: `Bearer ${session.access_token}` },
          });
          if (tplRes.ok) {
            const tplData = await tplRes.json();
            const offerTpl = (tplData.templates || []).find(t =>
              t.kind === 'offer' || (t.name || '').toLowerCase().includes('offer')
            );
            if (offerTpl) {
              const genRes = await fetch('/api/deal-templates?action=instantiate', {
                method: 'POST',
                headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  template_id: offerTpl.id,
                  contact_id: leadId,
                  name: `Offer - ${cleanPatch.property_address}`,
                }),
              });
              if (genRes.ok) showToast?.('Offer document auto-generated');
            }
          }
        } catch { /* non-fatal — document generation is a convenience */ }
      }

      // Auto-create Deal in Transactions when offer/LOI is accepted
      if (shouldCreateDeal && dealData) {
        try {
          const lead = leads.find(l => l.id === leadId);
          const isCommercialDeal = dealData.deal_type === 'commercial_sale' || dealData.deal_type === 'commercial_lease';

          const dealPayload = {
            contact_id: leadId,
            property_address: dealData.property_address,
            ...(isCommercialDeal ? {
              // Commercial deal — use deal_type and status from LOI accepted data
              deal_type: dealData.deal_type,
              status: dealData.status || 'due_diligence',
              commercial_sub_type: dealData.commercial_sub_type,
              loi_submitted_date: dealData.loi_submitted_date,
              loi_accepted_date: dealData.loi_accepted_date,
              due_diligence_start: dealData.due_diligence_start,
              // Lease fields
              leasable_sf: dealData.leasable_sf,
              lease_rate_sf_year: dealData.lease_rate_sf_year,
              lease_term_months: dealData.lease_term_months,
              // Sale fields
              offer_price: dealData.offer_price,
              sale_price: dealData.sale_price,
            } : {
              // Residential deal — existing behavior
              status: 'attorney_review',
              offer_price: dealData.offer_price,
              sale_price: dealData.sale_price,
              closing_date: dealData.closing_date,
              offer_date: dealData.offer_date,
              attorney_name: dealData.attorney_name,
              attorney_phone: dealData.attorney_phone,
              acceptance_date: new Date().toISOString().split('T')[0],
              attorney_review_start: new Date().toISOString().split('T')[0],
            }),
          };
          const dealRes = await fetch('/api/leads?action=convert_to_deal', {
            method: 'POST',
            headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify(dealPayload),
          });
          if (dealRes.ok) {
            const dest = isCommercialDeal ? 'Due Diligence' : 'Attorney Review';
            showToast?.(`Deal created in Transactions → ${dest}`);
          } else {
            const d = await dealRes.json();
            showToast?.(`Lead updated but deal creation failed: ${d.error}`);
          }
        } catch (dealErr) {
          showToast?.(`Lead updated but deal creation failed: ${dealErr.message}`);
        }
      }
    } catch (e) {
      showToast?.(e.message);
      load();
    }
  };

  const bulkAssignStatus = async (status) => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (demoMode) {
      setLeads(ls => ls.map(l => ids.includes(l.id) ? { ...l, lead_status: status } : l));
      clearSelection();
      return;
    }
    try {
      const { data: { session } } = await supabase.auth.getSession();
      await fetch('/api/leads?bulk=1', {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids, patch: { lead_status: status } }),
      });
      clearSelection();
      load();
    } catch (e) { showToast?.(e.message); }
  };

  const bulkAddTag = async (tag) => {
    const ids = Array.from(selected);
    if (!ids.length || !tag) return;
    if (demoMode) {
      setLeads(ls => ls.map(l => ids.includes(l.id) ? { ...l, tags: Array.from(new Set([...(l.tags || []), tag])) } : l));
      clearSelection();
      return;
    }
    try {
      const { data: { session } } = await supabase.auth.getSession();
      await fetch('/api/leads?bulk=1', {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids, patch: { tags_add: [tag] } }),
      });
      clearSelection();
      load();
    } catch (e) { showToast?.(e.message); }
  };

  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} lead${ids.length > 1 ? 's' : ''}? This cannot be undone.`)) return;
    if (demoMode) {
      setLeads(ls => ls.filter(l => !ids.includes(l.id)));
      clearSelection();
      return;
    }
    try {
      const { data: { session } } = await supabase.auth.getSession();
      for (const id of ids) {
        await fetch(`/api/leads?id=${id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
      }
      clearSelection();
      load();
    } catch (e) { showToast?.(e.message); }
  };

  // Determine which contacts to show in the smart-list detail view
  const smartListEnriched = useMemo(() => {
    return smartListContacts.map(l => ({
      ...l,
      _score: l.lead_score ?? computeLeadScore(l),
      _staleness: stalenessOf(l),
      _days_since: daysSinceContact(l),
    }));
  }, [smartListContacts]);

  // ── Inbox pending count (for badge) ──
  const [inboxPendingCount, setInboxPendingCount] = useState(0);

  // Handle external navigation (e.g. TopBar calendar shortcut)
  useEffect(() => {
    if (initialTab) {
      setCrmTab(initialTab);
      onTabConsumed?.();
    }
  }, [initialTab, onTabConsumed]);

  return (
    <div style={{ padding: '20px 22px', maxWidth: 1200, margin: '0 auto' }}>
      {/* Sticky header zone — title + sub-nav + view toggle + actions + filters */}
      <div style={{
        position: 'sticky', top: 56, zIndex: 20,
        background: theme.colors.bg,
        paddingTop: 8, paddingBottom: 6,
        marginLeft: -22, marginRight: -22,
        paddingLeft: 22, paddingRight: 22,
        boxShadow: '0 1px 0 rgba(0,0,0,0.05)',
      }}>
        {/* Top row: title + actions */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 10 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: theme.colors.text, margin: 0 }}>
            CRM
          </h1>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* View toggle (on Residential or Commercial board) */}
            {(crmTab === 'people' || crmTab === 'commercial') && !activeSmartListId && (
              <div data-tour="board-view-toggle" style={{ display: 'flex', background: '#EEF2F7', borderRadius: 8, padding: 2 }}>
                {[
                  { k: 'list',   Icon: ListIcon,  lbl: 'List' },
                  { k: 'kanban', Icon: KanbanIcon, lbl: 'Kanban' },
                ].map(item => {
                  const IconCmp = item.Icon;
                  const active = viewMode === item.k;
                  return (
                    <button key={item.k} onClick={() => setViewModePersisted(item.k)} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      padding: '6px 10px', border: 'none', borderRadius: 6,
                      background: active ? theme.colors.card : 'transparent',
                      color: active ? theme.colors.text : theme.colors.textLight,
                      fontSize: 11, fontWeight: 600, cursor: 'pointer',
                      boxShadow: active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                    }}>
                      <IconCmp size={13} /> {item.lbl}
                    </button>
                  );
                })}
              </div>
            )}

            <button data-tour="add-lead" onClick={() => setShowForm(true)} style={primaryBtn()}>
              <Plus size={15} /> Add Lead
            </button>
            <button onClick={() => setShowImport(true)} style={ghostBtn()}>
              <Upload size={15} /> Import CSV
            </button>
            <button onClick={async () => {
              try {
                const { data: { session } } = await supabase.auth.getSession();
                if (!session) { showToast?.('Not signed in'); return; }
                const url = `/api/export-contacts?${new URLSearchParams({
                  ...(filterStatus ? { filter_status: filterStatus } : {}),
                  ...(filterType ? { filter_type: filterType } : {}),
                  ...(filterTag ? { filter_tag: filterTag } : {}),
                  ...(searchQuery ? { search: searchQuery } : {}),
                }).toString()}`;
                const r = await fetch(url, { headers: { Authorization: `Bearer ${session.access_token}` } });
                if (!r.ok) { const d = await r.json(); showToast?.(d.error || 'Export failed'); return; }
                const blob = await r.blob();
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = `trovva_contacts_${new Date().toISOString().split('T')[0]}.csv`;
                a.click();
                URL.revokeObjectURL(a.href);
                showToast?.('Contacts exported');
              } catch (e) { showToast?.(e.message); }
            }} style={ghostBtn()}>
              <Download size={15} /> Export CSV
            </button>
            {/* Brain Chat available via floating button */}
          </div>
        </div>

        {/* ── Horizontal sub-navigation (FUB-style) ── */}
        <div data-tour="lead-nav" style={{
          display: 'flex', gap: 0, borderBottom: `2px solid ${theme.colors.border}`,
          marginBottom: 12,
        }}>
          {[
            { id: 'inbox',        label: 'Inbox' },
            { id: 'people',       label: 'Residential' },
            { id: 'commercial',   label: 'Commercial' },
            { id: 'tasks',        label: 'Tasks' },
            { id: 'calendar',     label: 'Calendar' },
            { id: 'automation',   label: 'Automation' },
          ]
            .filter(tab => !tab.featureKey || isFeatureVisible(tab.featureKey, org?.simple_mode))
            .map(tab => {
            const active = crmTab === tab.id;
            return (
              <button key={tab.id} onClick={() => { setCrmTab(tab.id); if (tab.id !== 'smart_lists') handleClearSmartList?.(); }} style={{
                padding: '8px 18px', border: 'none', cursor: 'pointer',
                background: 'transparent',
                color: active ? theme.colors.primary : theme.colors.textLight,
                fontSize: 13, fontWeight: active ? 700 : 500,
                borderBottom: active ? `2px solid ${theme.colors.primary}` : '2px solid transparent',
                marginBottom: -2,
                transition: 'color 0.15s, border-color 0.15s',
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}>
                {tab.label}
                {tab.id === 'inbox' && inboxPendingCount > 0 && (
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    minWidth: 18, height: 18, borderRadius: 9, padding: '0 5px',
                    background: theme.colors.danger, color: 'white',
                    fontSize: 10, fontWeight: 700, lineHeight: 1,
                  }}>{inboxPendingCount > 99 ? '99+' : inboxPendingCount}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Filters row (Residential or Commercial board, not in smart list mode) */}
        {(crmTab === 'people' || crmTab === 'commercial') && !activeSmartListId && (
          <div data-tour="lead-filters" style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', flex: '1 1 240px', minWidth: 200 }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: 11, color: theme.colors.textLight }} />
              <input
                value={q} onChange={e => setQ(e.target.value)}
                placeholder="Search name / email / phone / source…"
                style={{ width: '100%', boxSizing: 'border-box', padding: '9px 10px 9px 34px', borderRadius: 10, border: `1px solid ${theme.colors.border}`, fontSize: 13 }}
              />
            </div>
            {/* Type filter: hidden on Commercial board (redundant), residential-only on Residential board */}
            {crmTab === 'people' && (
              <FilterSelect value={filterType} onChange={setFilterType} label="Type"
                options={[['', 'All types'], ['buyer','Buyer'], ['seller','Seller'], ['renter','Renter'], ['pm_owner','PM owner'], ['investor','Investor']]} />
            )}
            {/* Status filter: uses the active pipeline's stages */}
            <FilterSelect value={filterStatus} onChange={setFilterStatus} label="Status"
              options={[['', 'All'], ...activePipeline.map(p => [p.key, p.label]), ['closed','Closed'], ['lost','Lost']]} />
            {allTags.length > 0 && (
              <FilterSelect value={filterTag} onChange={setFilterTag} label="Tag"
                options={[['', 'All tags'], ...allTags.map(t => [t, t])]} />
            )}
            {isBrokerOrAdmin && agents.length > 0 && (
              <AgentFilterCombo
                agents={agents}
                value={boardAgent}
                onChange={setBoardAgent}
                mineLabel="My Leads"
                allLabel="All Leads"
              />
            )}
            <button onClick={load} title="Refresh" style={{
              padding: '9px 12px', borderRadius: 10,
              border: `1px solid ${theme.colors.border}`, background: theme.colors.card,
              cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5,
            }}>
              <RefreshCw size={13} />
            </button>
            {!demoMode && crmTab === 'people' && (
              <SavedViewsPicker
                views={savedViews}
                activeId={activeSavedViewId}
                onApply={applyView}
                onSave={saveCurrentAsView}
                onDelete={deleteSavedView}
              />
            )}
          </div>
        )}
      </div>{/* /sticky header zone */}

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <BulkBar
          count={selected.size}
          onClear={clearSelection}
          onStatus={bulkAssignStatus}
          onTag={bulkAddTag}
          onDelete={bulkDelete}
          existingTags={allTags}
        />
      )}

      {error && <div style={{ padding: 12, background: '#FEF2F2', color: theme.colors.danger, borderRadius: 10, marginBottom: 12 }}>{error}</div>}

      {/* ════════════ INBOX TAB ════════════ */}
      {crmTab === 'inbox' && (
        <InboxTab
          demoMode={demoMode}
          showToast={showToast}
          onOpenLead={(contactId, inboxItemId) => { setOpenLeadId(contactId); setOpenInboxItemId(inboxItemId || null); }}
          agents={agents}
          onCountUpdate={setInboxPendingCount}
        />
      )}

      {/* ════════════ PEOPLE TAB ════════════ */}
      {crmTab === 'people' && !activeSmartListId && (
        <>
          {loading ? (
            <LoadingSpinner variant="section" label="Loading leads..." />
          ) : viewMode === 'kanban' ? (
            <Kanban
              byStatus={byStatus}
              onOpen={setOpenLeadId}
              onDrop={handleDrop}
              selected={selected}
              toggleSelect={toggleSelect}
            />
          ) : (
            <ListTable
              rows={filtered}
              onOpen={setOpenLeadId}
              selected={selected}
              toggleSelect={toggleSelect}
            />
          )}
          {filtered.length === 0 && !loading && (
            <>
              {q || filterType || filterStatus || filterTag ? (
                <EmptyState type="search" />
              ) : (
                <EmptyState type="leads" onAction={() => setShowForm(true)} />
              )}
            </>
          )}
        </>
      )}

      {/* ════════════ COMMERCIAL BOARD ════════════ */}
      {crmTab === 'commercial' && (
        <>
          {/* Sub-type filter chips */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
            {[{ key: '', label: 'All' }, ...COMMERCIAL_SUB_TYPES].map(st => {
              const active = commercialSubFilter === st.key;
              return (
                <button
                  key={st.key}
                  onClick={() => setCommercialSubFilter(st.key)}
                  style={{
                    padding: '6px 16px', borderRadius: 20,
                    border: active ? 'none' : `1px solid ${theme.colors.border}`,
                    background: active ? (st.color || theme.colors.text) : 'transparent',
                    color: active ? 'white' : (st.color || theme.colors.textLight),
                    fontSize: 12, fontWeight: active ? 700 : 500,
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}
                >
                  {st.label}
                </button>
              );
            })}
          </div>

          {loading ? (
            <LoadingSpinner variant="section" label="Loading commercial leads..." />
          ) : viewMode === 'kanban' ? (
            <Kanban
              byStatus={byStatus}
              onOpen={setOpenLeadId}
              onDrop={handleDrop}
              selected={selected}
              toggleSelect={toggleSelect}
              pipeline={COMMERCIAL_CRM_PIPELINE}
            />
          ) : (
            <ListTable
              rows={filtered}
              onOpen={setOpenLeadId}
              selected={selected}
              toggleSelect={toggleSelect}
            />
          )}
          {filtered.length === 0 && !loading && (
            <>
              {q || commercialSubFilter || filterStatus || filterTag ? (
                <EmptyState type="search" />
              ) : (
                <EmptyState type="leads" onAction={() => setShowForm(true)} actionLabel="Add Commercial Lead" />
              )}
            </>
          )}
        </>
      )}

      {/* ════════════ TASKS TAB ════════════ */}
      {crmTab === 'tasks' && (
        <TasksTab demoMode={demoMode} showToast={showToast} agents={agents} onOpenLead={setOpenLeadId} />
      )}

      {/* ════════════ CALENDAR TAB ════════════ */}
      {crmTab === 'calendar' && (
        <CalendarView />
      )}

      {/* ════════════ AUTOMATION TAB (unified) ════════════ */}
      {crmTab === 'automation' && (
        <>
          {/* Section toggle */}
          <div style={{
            display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap',
          }}>
            {[
              { id: 'segments',     label: 'Segments' },
              { id: 'action_plans', label: 'Action Plans' },
              { id: 'routing',      label: 'Routing' },
              { id: 'automations',  label: 'Automations' },
            ].map(sec => {
              const active = automationSection === sec.id;
              return (
                <button key={sec.id} onClick={() => setAutomationSection(sec.id)}
                  style={{
                    padding: '8px 18px', borderRadius: 8,
                    border: active ? 'none' : `1px solid ${theme.colors.border}`,
                    background: active ? theme.colors.primary : 'transparent',
                    color: active ? 'white' : theme.colors.text,
                    fontSize: 13, fontWeight: active ? 700 : 500,
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}>
                  {sec.label}
                </button>
              );
            })}
          </div>

          {automationSection === 'segments' && (
            <SmartListsSection
              lists={smartLists}
              loading={smartListsLoading}
              activeId={activeSmartListId}
              onSelect={handleSelectSmartList}
              onClear={handleClearSmartList}
              onCreateNew={() => setShowSmartListEditor(true)}
              onEdit={(sl) => { setEditingSmartList(sl); setShowSmartListEditor(true); }}
              onDelete={handleDeleteSmartList}
              smartListContacts={smartListEnriched}
              smartListLoading={smartListLoading}
              onOpenLead={setOpenLeadId}
              selected={selected}
              toggleSelect={toggleSelect}
            />
          )}
          {automationSection === 'action_plans' && (
            <ActionPlansTab demoMode={demoMode} showToast={showToast} />
          )}
          {automationSection === 'routing' && (
            <LeadRoutingTab demoMode={demoMode} showToast={showToast} agents={agents} />
          )}
          {automationSection === 'automations' && (
            <AutomationsTab demoMode={demoMode} showToast={showToast} />
          )}
        </>
      )}

      {/* ── Modals & Drawers ── */}
      {showForm && <AddLeadForm onClose={() => setShowForm(false)} onSave={handleLeadSaved} defaultCommercial={crmTab === 'commercial'} />}
      <CsvImport
        open={showImport}
        onClose={() => setShowImport(false)}
        onImported={() => { setShowImport(false); load(); }}
        onNavigateTab={(tab) => { setCrmTab(tab); load(); }}
      />

      {openLeadId && !demoMode && createPortal(
          /* Always use 3-pane central detail view — consistent for Inbox and My Board */
          <div style={{
            position: 'fixed', inset: 0, zIndex: 999,
            display: 'flex', justifyContent: 'center', alignItems: 'stretch',
          }}>
            <div style={{
              position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)',
              backdropFilter: 'blur(2px)',
            }} onClick={() => { setOpenLeadId(null); setOpenInboxItemId(null); }} />
            <div style={{
              position: 'relative', width: '92%', maxWidth: 1200,
              margin: '24px 0', borderRadius: 16, overflow: 'hidden',
              boxShadow: '0 25px 60px rgba(0,0,0,0.25)',
              background: theme.colors.card, zIndex: 1,
            }}>
              <ContactDetailView
                leadId={openLeadId}
                onClose={() => { setOpenLeadId(null); setOpenInboxItemId(null); }}
                onChange={() => { load(); if (activeSmartListId) loadSmartListContacts(activeSmartListId); }}
                demoMode={demoMode}
                showToast={showToast}
              />
            </div>
          </div>,
        document.body
      )}

      {stageGate && createPortal(
        <StageGateModal
          lead={stageGate.lead}
          targetStatus={stageGate.targetStatus}
          onCancel={() => setStageGate(null)}
          onConfirm={async (patch) => {
            await commitStageChange(stageGate.lead.id, patch);
            setStageGate(null);
          }}
        />,
        document.body
      )}

      {showSmartListEditor && createPortal(
        <SmartListEditorModal
          smartList={editingSmartList}
          agents={agents}
          onSave={handleSaveSmartList}
          onClose={() => { setShowSmartListEditor(false); setEditingSmartList(null); }}
        />,
        document.body
      )}

      {/* Per-tab guided tour */}
      {CRM_TAB_TOURS[crmTab] && (
        <GuidedTour
          key={`crm-${crmTab}`}
          tourId={`crm_${crmTab}`}
          steps={CRM_TAB_TOURS[crmTab]}
        />
      )}

      <style>{`@keyframes spin { from{transform:rotate(0)} to{transform:rotate(360deg)} }`}</style>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Lead Inbox — triage queue
// ──────────────────────────────────────────────────────────────────

const URGENCY_COLORS = { green: '#22C55E', yellow: '#EAB308', orange: '#F97316', red: '#EF4444' };
const SOURCE_LABELS = {
  zillow: 'Zillow', realtor_com: 'Realtor.com', facebook: 'Facebook', email: 'Email',
  trulia: 'Trulia', redfin: 'Redfin', walk_in: 'Walk-in', manual: 'Manual',
  bright_mls: 'Bright MLS', wix_website: 'Website', homes_com: 'Homes.com',
};
const TYPE_LABELS = { buyer: 'Buyer', seller: 'Seller', renter: 'Renter', commercial: 'Commercial', investor: 'Investor', pm_owner: 'PM Owner' };
const SNOOZE_OPTIONS = [
  { label: '1 hour', ms: 3600000 },
  { label: '4 hours', ms: 14400000 },
  { label: 'Tomorrow 9 AM', ms: null }, // calculated at click time
  { label: '2 days', ms: 172800000 },
];

function InboxTab({ demoMode, showToast, onOpenLead, agents, onCountUpdate }) {
  const [inboxMode, setInboxMode] = useState('triage'); // 'triage' | 'activity'
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, snoozed: 0, unassigned: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [showPool, setShowPool] = useState(false);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [processing, setProcessing] = useState(new Set()); // item IDs being processed
  const [softDone, setSoftDone] = useState({}); // { itemId: Set(['Called','Texted',...]) }
  const [snoozeMenuId, setSnoozeMenuId] = useState(null);
  const [passMenuId, setPassMenuId] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [reassignMenuId, setReassignMenuId] = useState(null);

  // Unified activity feed state
  const [feedItems, setFeedItems] = useState([]);
  const [feedCounts, setFeedCounts] = useState({ leads: 0, sms: 0, email: 0, calls: 0, deals: 0, total_unread: 0 });
  const [feedFilter, setFeedFilter] = useState('all');
  const [feedLoading, setFeedLoading] = useState(false);

  const isBroker = userRole === 'broker' || userRole === 'admin';

  // Fetch user role
  useEffect(() => {
    if (demoMode) return;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;
        const { data: { user } } = await supabase.auth.getUser(session.access_token);
        if (!user) return;
        const { data: me } = await supabase.from('users').select('role').eq('id', user.id).maybeSingle();
        if (me?.role) setUserRole(me.role);
      } catch { /* noop */ }
    })();
  }, [demoMode]);

  // ── Unified Activity Feed loader ──
  const loadFeed = useCallback(async () => {
    if (demoMode) return;
    setFeedLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const params = new URLSearchParams({ filter: feedFilter, limit: '80' });
      const r = await fetch(`/api/inbox-feed?${params}`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const data = await r.json();
      if (r.ok) {
        setFeedItems(data.items || []);
        setFeedCounts(data.counts || {});
        // Update parent badge with total unread
        onCountUpdate?.(data.counts?.total_unread || 0);
      }
    } catch { /* noop */ }
    finally { setFeedLoading(false); }
  }, [demoMode, feedFilter]);

  useEffect(() => {
    if (inboxMode === 'activity') loadFeed();
  }, [inboxMode, loadFeed]);

  const loadInbox = useCallback(async () => {
    if (demoMode) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not signed in');
      const params = new URLSearchParams({ status: statusFilter, limit: '100' });
      if (showPool && isBroker) params.set('pool', '1');
      const r = await fetch(`/api/inbox?${params}`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Failed to load inbox');
      setItems(data.items || []);
      setCounts(data.counts || { pending: 0, snoozed: 0, unassigned: 0 });
      onCountUpdate?.(data.counts?.pending || 0);
    } catch (e) {
      showToast?.(`Inbox: ${e.message}`);
    } finally { setLoading(false); }
  }, [demoMode, statusFilter, showPool, isBroker, showToast]);

  useEffect(() => { loadInbox(); }, [loadInbox]);

  // Soft actions keep the item in the list; hard actions reload the list
  const SOFT_ACTIONS = new Set(['contact_call', 'contact_text', 'contact_email']);

  // Process an action on one item
  const processAction = async (itemId, action, extra = {}) => {
    setProcessing(prev => new Set(prev).add(itemId));
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not signed in');
      const r = await fetch('/api/inbox', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_id: itemId, action, ...extra }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Action failed');

      if (SOFT_ACTIONS.has(action)) {
        // Soft: show checkmark feedback, don't reload list
        const label = action === 'contact_call' ? 'Called' : action === 'contact_text' ? 'Texted' : 'Emailed';
        setSoftDone(prev => {
          const s = new Set(prev[itemId] || []);
          s.add(label);
          return { ...prev, [itemId]: s };
        });
        showToast?.(`${label} logged — lead stays in inbox`);
      } else {
        // Hard: reload the list (item removed)
        await loadInbox();
        setSoftDone(prev => { const next = { ...prev }; delete next[itemId]; return next; });
        setSnoozeMenuId(null);
        setPassMenuId(null);
        setReassignMenuId(null);
      }
    } catch (e) {
      showToast?.(e.message);
    } finally {
      setProcessing(prev => { const s = new Set(prev); s.delete(itemId); return s; });
    }
  };

  // Batch process
  const batchProcess = async (action) => {
    if (selectedItems.size === 0) return;
    const ids = Array.from(selectedItems);
    setProcessing(new Set(ids));
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not signed in');
      const r = await fetch('/api/inbox', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_ids: ids, action }),
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.error || 'Batch failed'); }
      setSelectedItems(new Set());
      await loadInbox();
    } catch (e) {
      showToast?.(e.message);
    } finally { setProcessing(new Set()); }
  };

  const toggleItem = (id) => {
    setSelectedItems(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };

  const selectAll = () => {
    if (selectedItems.size === items.length) setSelectedItems(new Set());
    else setSelectedItems(new Set(items.map(i => i.id)));
  };

  // Calculate snooze time
  const getSnoozeUntil = (opt) => {
    if (opt.ms) return new Date(Date.now() + opt.ms).toISOString();
    // "Tomorrow 9 AM"
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    return d.toISOString();
  };

  if (demoMode) {
    return <ComingSoon label="Lead Inbox" desc="Inbox requires live data. Connect your lead sources to see incoming leads here." />;
  }

  // ── Unified Activity Feed View ──
  if (inboxMode === 'activity') {
    const FEED_CHANNEL_ICON = {
      lead: { icon: UserPlus, color: '#22C55E', bg: '#22C55E14', label: 'New Lead' },
      sms: { icon: MessageSquare, color: '#3B82F6', bg: '#3B82F614', label: 'SMS' },
      email: { icon: Mail, color: '#8B5CF6', bg: '#8B5CF614', label: 'Email' },
      call: { icon: Phone, color: '#F59E0B', bg: '#F59E0B14', label: 'Missed Call' },
      deal_event: { icon: ArrowRight, color: '#06B6D4', bg: '#06B6D414', label: 'Deal' },
    };

    const timeAgo = (ts) => {
      const secs = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
      if (secs < 60) return 'just now';
      if (secs < 3600) return `${Math.floor(secs / 60)}m`;
      if (secs < 86400) return `${Math.floor(secs / 3600)}h`;
      return `${Math.floor(secs / 86400)}d`;
    };

    return (
      <div>
        {/* Mode toggle + channel filters */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
          {/* Triage / Feed toggle */}
          <div data-tour="inbox-toggle" style={{ display: 'flex', gap: 0, background: '#EEF2F7', borderRadius: 8, padding: 2 }}>
            {[
              { key: 'triage', label: 'Triage' },
              { key: 'activity', label: 'Feed' },
            ].map(m => (
              <button key={m.key} onClick={() => setInboxMode(m.key)} style={{
                padding: '6px 14px', border: 'none', borderRadius: 6,
                background: inboxMode === m.key ? theme.colors.card : 'transparent',
                color: inboxMode === m.key ? theme.colors.text : theme.colors.textLight,
                fontSize: 12, fontWeight: 600, cursor: 'pointer',
                boxShadow: inboxMode === m.key ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              }}>{m.label}</button>
            ))}
          </div>

          <div style={{ width: 1, height: 20, background: theme.colors.border }} />

          {/* Channel filter pills */}
          {[
            { key: 'all', label: 'All', count: feedCounts.total_unread },
            { key: 'leads', label: 'Leads', count: feedCounts.leads },
            { key: 'sms', label: 'SMS', count: feedCounts.sms },
            { key: 'email', label: 'Email', count: feedCounts.email },
            { key: 'calls', label: 'Calls', count: feedCounts.calls },
            { key: 'deals', label: 'Deals', count: feedCounts.deals },
          ].map(f => {
            const active = feedFilter === f.key;
            return (
              <button key={f.key} onClick={() => setFeedFilter(f.key)} style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '5px 10px', border: `1px solid ${active ? theme.colors.primary + '40' : theme.colors.border}`,
                borderRadius: 20, background: active ? theme.colors.primary + '08' : 'transparent',
                color: active ? theme.colors.primary : theme.colors.textLight,
                fontSize: 11, fontWeight: 600, cursor: 'pointer',
              }}>
                {f.label}
                {f.count > 0 && <span style={{
                  background: active ? theme.colors.primary : '#94A3B8',
                  color: 'white', borderRadius: 8, padding: '0 5px',
                  fontSize: 9, fontWeight: 700, lineHeight: '16px',
                }}>{f.count}</span>}
              </button>
            );
          })}

          <div style={{ flex: 1 }} />
          <button onClick={loadFeed} title="Refresh" style={{
            padding: '7px 10px', borderRadius: 8,
            border: `1px solid ${theme.colors.border}`, background: theme.colors.card,
            cursor: 'pointer', display: 'inline-flex', alignItems: 'center',
          }}><RefreshCw size={13} /></button>
        </div>

        {/* Feed items */}
        {feedLoading && <LoadingSpinner variant="section" label="Loading activity..." />}

        {!feedLoading && feedItems.length === 0 && (
          <div style={{ padding: '50px 20px', textAlign: 'center' }}>
            <div style={{
              width: 56, height: 56, borderRadius: 28, background: `${theme.colors.success}14`,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16,
            }}>
              <Inbox size={24} color={theme.colors.success} />
            </div>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: theme.colors.text, margin: '0 0 6px' }}>All caught up!</h3>
            <p style={{ fontSize: 13, color: theme.colors.textLight, maxWidth: 360, margin: '0 auto' }}>
              No new activity. Leads, SMS replies, emails, and missed calls will appear here.
            </p>
          </div>
        )}

        {!feedLoading && feedItems.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {feedItems.map(item => {
              const ch = FEED_CHANNEL_ICON[item.type] || FEED_CHANNEL_ICON.lead;
              const Icon = ch.icon;
              return (
                <button key={item.id} onClick={() => {
                  if (item.contact_id) onOpenLead(item.contact_id, item.inbox_item_id || null);
                }} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
                  background: item.is_unread ? `${theme.colors.primary}04` : theme.colors.card,
                  border: `1px solid ${item.is_unread ? theme.colors.primary + '20' : theme.colors.border}`,
                  borderRadius: 10, cursor: 'pointer', textAlign: 'left', width: '100%',
                  transition: 'background 0.15s',
                }}>
                  {/* Unread dot */}
                  <div style={{ width: 8, flexShrink: 0 }}>
                    {item.is_unread && <div style={{
                      width: 8, height: 8, borderRadius: 4, background: theme.colors.primary,
                    }} />}
                  </div>

                  {/* Channel icon */}
                  <div style={{
                    width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                    background: ch.bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Icon size={16} color={ch.color} />
                  </div>

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{
                        fontSize: 13, fontWeight: item.is_unread ? 700 : 600,
                        color: theme.colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {item.contact_name}
                      </span>
                      <span style={{
                        fontSize: 9, fontWeight: 600, padding: '1px 6px', borderRadius: 4,
                        background: ch.bg, color: ch.color, flexShrink: 0,
                      }}>{ch.label}</span>
                    </div>
                    <div style={{
                      fontSize: 12, color: theme.colors.textLight, marginTop: 2,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {item.subject !== ch.label ? `${item.subject} — ` : ''}{item.preview}
                    </div>
                  </div>

                  {/* Time */}
                  <div style={{ fontSize: 11, color: theme.colors.textLight, fontWeight: 500, flexShrink: 0 }}>
                    {timeAgo(item.timestamp)}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ── Triage Mode (existing lead inbox) ──
  return (
    <div>
      {/* Inbox toolbar */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        {/* Triage / Feed toggle */}
        <div style={{ display: 'flex', gap: 0, background: '#EEF2F7', borderRadius: 8, padding: 2 }}>
          {[
            { key: 'triage', label: 'Triage' },
            { key: 'activity', label: 'Feed' },
          ].map(m => (
            <button key={m.key} onClick={() => setInboxMode(m.key)} style={{
              padding: '6px 14px', border: 'none', borderRadius: 6,
              background: inboxMode === m.key ? theme.colors.card : 'transparent',
              color: inboxMode === m.key ? theme.colors.text : theme.colors.textLight,
              fontSize: 12, fontWeight: 600, cursor: 'pointer',
              boxShadow: inboxMode === m.key ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
            }}>{m.label}</button>
          ))}
        </div>

        <div style={{ width: 1, height: 20, background: theme.colors.border }} />

        {/* Status pills */}
        <div style={{ display: 'flex', gap: 0, background: '#EEF2F7', borderRadius: 8, padding: 2 }}>
          {[
            { key: 'pending', label: 'Pending', count: counts.pending },
            { key: 'snoozed', label: 'Snoozed', count: counts.snoozed },
            { key: 'all', label: 'All' },
          ].map(f => {
            const active = statusFilter === f.key && !showPool;
            return (
              <button key={f.key} onClick={() => { setStatusFilter(f.key); setShowPool(false); }} style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '6px 12px', border: 'none', borderRadius: 6,
                background: active ? theme.colors.card : 'transparent',
                color: active ? theme.colors.text : theme.colors.textLight,
                fontSize: 12, fontWeight: 600, cursor: 'pointer',
                boxShadow: active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              }}>
                {f.label}
                {f.count > 0 && <span style={{
                  background: active ? theme.colors.primary : theme.colors.textLight,
                  color: 'white', borderRadius: 8, padding: '1px 6px',
                  fontSize: 10, fontWeight: 700, marginLeft: 2,
                }}>{f.count}</span>}
              </button>
            );
          })}
        </div>

        {/* Unassigned pool toggle (broker only) */}
        {isBroker && (
          <button onClick={() => { setShowPool(!showPool); setStatusFilter('pending'); }} style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '6px 12px', borderRadius: 8,
            border: `1px solid ${showPool ? theme.colors.primary : theme.colors.border}`,
            background: showPool ? `${theme.colors.primary}10` : theme.colors.card,
            color: showPool ? theme.colors.primary : theme.colors.textLight,
            fontSize: 12, fontWeight: 600, cursor: 'pointer',
          }}>
            <Users size={13} />
            Unassigned Pool
            {counts.unassigned > 0 && <span style={{
              background: theme.colors.warning, color: '#000', borderRadius: 8,
              padding: '1px 6px', fontSize: 10, fontWeight: 700,
            }}>{counts.unassigned}</span>}
          </button>
        )}

        <div style={{ flex: 1 }} />

        {/* Refresh */}
        <button onClick={loadInbox} title="Refresh inbox" style={{
          padding: '7px 10px', borderRadius: 8,
          border: `1px solid ${theme.colors.border}`, background: theme.colors.card,
          cursor: 'pointer', display: 'inline-flex', alignItems: 'center',
        }}>
          <RefreshCw size={13} />
        </button>
      </div>

      {/* Batch actions bar */}
      {selectedItems.size > 0 && (
        <div style={{
          display: 'flex', gap: 8, alignItems: 'center', padding: '8px 14px',
          background: `${theme.colors.primary}08`, border: `1px solid ${theme.colors.primary}30`,
          borderRadius: 10, marginBottom: 12,
        }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: theme.colors.primary }}>
            {selectedItems.size} selected
          </span>
          <button onClick={() => batchProcess('claim')} style={batchBtn(theme)}>
            <Check size={12} /> Claim All
          </button>
          <button onClick={() => setSelectedItems(new Set())} style={{
            ...batchBtn(theme), color: theme.colors.textLight, background: 'transparent',
          }}>
            <X size={12} /> Clear
          </button>
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <LoadingSpinner variant="section" label="Loading inbox..." />
      )}

      {/* Empty state */}
      {!loading && items.length === 0 && (
        <div style={{ padding: '50px 20px', textAlign: 'center' }}>
          <div style={{
            width: 56, height: 56, borderRadius: 28, background: `${theme.colors.success}14`,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16,
          }}>
            <Inbox size={24} color={theme.colors.success} />
          </div>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: theme.colors.text, margin: '0 0 6px' }}>
            {statusFilter === 'pending' ? 'Inbox Zero!' : 'No items'}
          </h3>
          <p style={{ fontSize: 13, color: theme.colors.textLight, maxWidth: 360, margin: '0 auto' }}>
            {statusFilter === 'pending'
              ? "You're all caught up. New leads will appear here as they come in."
              : `No ${statusFilter} items found.`}
          </p>
        </div>
      )}

      {/* Inbox items list */}
      {!loading && items.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* Header row */}
          <div style={{
            display: 'flex', alignItems: 'center', padding: '6px 12px', gap: 10,
            fontSize: 11, fontWeight: 600, color: theme.colors.textLight, textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}>
            <div style={{ width: 28 }}>
              <button onClick={selectAll} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: theme.colors.textLight }}>
                {selectedItems.size === items.length ? <CheckSquare size={14} /> : <Square size={14} />}
              </button>
            </div>
            <div style={{ width: 20 }} />
            <div style={{ width: 64 }}>Age</div>
            <div style={{ flex: '1 1 200px' }}>Contact</div>
            <div style={{ width: 90 }}>Lead Type</div>
            <div style={{ width: 90 }}>Source</div>
            <div style={{ width: 70 }}>Type</div>
            <div style={{ flex: '1 1 180px' }}>Preview</div>
            <div style={{ width: 280, textAlign: 'center' }}>Actions</div>
          </div>

          {items.map(item => {
            const isProcessing = processing.has(item.id);
            const isSelected = selectedItems.has(item.id);
            const contact = item.contact || {};
            const name = [contact.first_name, contact.last_name].filter(Boolean).join(' ') || 'Unknown';

            return (
              <div key={item.id} data-tour={items.indexOf(item) === 0 ? 'inbox-row' : undefined} style={{
                display: 'flex', alignItems: 'center', padding: '10px 12px', gap: 10,
                background: isSelected ? `${theme.colors.primary}06` : theme.colors.card,
                borderRadius: 10, border: `1px solid ${isSelected ? theme.colors.primary + '30' : theme.colors.border}`,
                opacity: isProcessing ? 0.5 : 1,
                transition: 'background 0.15s, opacity 0.15s',
              }}>
                {/* Checkbox */}
                <div style={{ width: 28 }}>
                  <button onClick={() => toggleItem(item.id)} style={{
                    background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                    color: isSelected ? theme.colors.primary : theme.colors.textLight,
                  }}>
                    {isSelected ? <CheckSquare size={15} /> : <Square size={15} />}
                  </button>
                </div>

                {/* Star */}
                <button onClick={() => processAction(item.id, item.is_starred ? 'unstar' : 'star')} style={{
                  background: 'none', border: 'none', cursor: 'pointer', padding: 0, width: 20,
                  color: item.is_starred ? '#EAB308' : theme.colors.textLight,
                }}>
                  <Star size={14} fill={item.is_starred ? '#EAB308' : 'none'} />
                </button>

                {/* Age urgency */}
                <div style={{ width: 64, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <div style={{
                    width: 8, height: 8, borderRadius: 4,
                    background: URGENCY_COLORS[item.age_urgency] || URGENCY_COLORS.green,
                  }} />
                  <span style={{ fontSize: 11, fontWeight: 600, color: theme.colors.text }}>
                    {item.age_label}
                  </span>
                </div>

                {/* Contact name (clickable → drawer) */}
                <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                  <button onClick={() => onOpenLead(item.contact_id, item.id)} style={{
                    background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                    fontSize: 13, fontWeight: 600, color: theme.colors.text, textAlign: 'left',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%',
                    display: 'block',
                  }}>
                    {name}
                  </button>
                  {item.assigned_note && (
                    <div style={{ fontSize: 10, color: theme.colors.warning, fontWeight: 500, marginTop: 2 }}>
                      {item.assigned_by ? 'Assigned: ' : ''}{item.assigned_note}
                    </div>
                  )}
                </div>

                {/* Lead Type badge — Residential or Commercial + Sale/Lease */}
                <div style={{ width: 90 }}>
                  {contact.lead_type === 'commercial' ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <span style={{
                        display: 'inline-block', padding: '2px 6px', borderRadius: 4,
                        background: '#8b5cf620', color: '#8b5cf6',
                        fontSize: 9, fontWeight: 700,
                      }}>COMMERCIAL</span>
                      {contact.commercial_sub_type && (
                        <span style={{
                          fontSize: 9, fontWeight: 500, color: '#8b5cf6',
                        }}>
                          {contact.commercial_sub_type === 'buyer_rep' || contact.commercial_sub_type === 'seller_rep' ? 'Sale' : 'Lease'}
                        </span>
                      )}
                    </div>
                  ) : (
                    <span style={{
                      display: 'inline-block', padding: '2px 6px', borderRadius: 4,
                      background: '#22c55e20', color: '#22c55e',
                      fontSize: 9, fontWeight: 700,
                    }}>RESIDENTIAL</span>
                  )}
                </div>

                {/* Source */}
                <div style={{ width: 90 }}>
                  <span style={{
                    display: 'inline-block', padding: '2px 8px', borderRadius: 6,
                    background: `${theme.colors.info}15`, color: theme.colors.info,
                    fontSize: 10, fontWeight: 600,
                  }}>
                    {SOURCE_LABELS[item.source_channel] || item.source_channel || '—'}
                  </span>
                </div>

                {/* Lead type (Buyer/Seller/Renter etc) */}
                <div style={{ width: 70, fontSize: 11, color: theme.colors.textLight, fontWeight: 500 }}>
                  {TYPE_LABELS[contact.lead_type] || contact.lead_type || '—'}
                </div>

                {/* Preview */}
                <div style={{ flex: '1 1 180px', minWidth: 0 }}>
                  {item.property_interest && (
                    <div style={{ fontSize: 11, fontWeight: 600, color: theme.colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.property_interest}
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: theme.colors.textLight, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.preview_body || item.preview_subject || '—'}
                  </div>
                </div>

                {/* Action buttons */}
                <div data-tour="inbox-actions" style={{ width: 280, display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'nowrap', position: 'relative' }}>
                  {statusFilter === 'pending' && (() => {
                    const done = softDone[item.id] || new Set();
                    return (
                    <>
                      <InboxActionBtn icon={done.has('Called') ? CheckCircle2 : Phone} label={done.has('Called') ? '✓ Called' : 'Call'} color="#22C55E"
                        onClick={() => processAction(item.id, 'contact_call')} disabled={isProcessing} done={done.has('Called')} />
                      <InboxActionBtn icon={done.has('Texted') ? CheckCircle2 : MessageSquare} label={done.has('Texted') ? '✓ Texted' : 'Text'} color="#3B82F6"
                        onClick={() => processAction(item.id, 'contact_text')} disabled={isProcessing} done={done.has('Texted')} />
                      <InboxActionBtn icon={done.has('Emailed') ? CheckCircle2 : Mail} label={done.has('Emailed') ? '✓ Emailed' : 'Email'} color="#8B5CF6"
                        onClick={() => processAction(item.id, 'contact_email')} disabled={isProcessing} done={done.has('Emailed')} />
                      <InboxActionBtn icon={Check} label="Claim" color={theme.colors.primary}
                        onClick={() => processAction(item.id, 'claim')} disabled={isProcessing} />

                      {/* Snooze dropdown */}
                      <div style={{ position: 'relative' }}>
                        <InboxActionBtn icon={Pause} label="Snooze" color="#F97316"
                          onClick={() => setSnoozeMenuId(snoozeMenuId === item.id ? null : item.id)} disabled={isProcessing} />
                        {snoozeMenuId === item.id && (
                          <div style={{
                            position: 'absolute', top: '100%', right: 0, zIndex: 50,
                            background: theme.colors.card, border: `1px solid ${theme.colors.border}`,
                            borderRadius: 8, boxShadow: theme.shadow.md, minWidth: 140, padding: '4px 0',
                            marginTop: 4,
                          }}>
                            {SNOOZE_OPTIONS.map(opt => (
                              <button key={opt.label} onClick={() => processAction(item.id, 'snooze', { snooze_until: getSnoozeUntil(opt) })} style={{
                                display: 'block', width: '100%', padding: '8px 14px',
                                background: 'transparent', border: 'none', cursor: 'pointer',
                                fontSize: 12, color: theme.colors.text, textAlign: 'left',
                              }}
                                onMouseEnter={e => { e.target.style.background = theme.colors.bg; }}
                                onMouseLeave={e => { e.target.style.background = 'transparent'; }}
                              >
                                {opt.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Pass dropdown */}
                      <div style={{ position: 'relative' }}>
                        <InboxActionBtn icon={XCircle} label="Pass" color={theme.colors.danger}
                          onClick={() => setPassMenuId(passMenuId === item.id ? null : item.id)} disabled={isProcessing} />
                        {passMenuId === item.id && (
                          <div style={{
                            position: 'absolute', top: '100%', right: 0, zIndex: 50,
                            background: theme.colors.card, border: `1px solid ${theme.colors.border}`,
                            borderRadius: 8, boxShadow: theme.shadow.md, minWidth: 150, padding: '4px 0',
                            marginTop: 4,
                          }}>
                            {['junk', 'duplicate', 'wrong_area', 'not_real', 'other'].map(reason => (
                              <button key={reason} onClick={() => processAction(item.id, 'pass', { pass_reason: reason })} style={{
                                display: 'block', width: '100%', padding: '8px 14px',
                                background: 'transparent', border: 'none', cursor: 'pointer',
                                fontSize: 12, color: theme.colors.text, textAlign: 'left', textTransform: 'capitalize',
                              }}
                                onMouseEnter={e => { e.target.style.background = theme.colors.bg; }}
                                onMouseLeave={e => { e.target.style.background = 'transparent'; }}
                              >
                                {reason.replace(/_/g, ' ')}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Reassign (broker only) */}
                      {isBroker && (
                        <div style={{ position: 'relative' }}>
                          <InboxActionBtn icon={UserPlus} label="Assign" color={theme.colors.textLight}
                            onClick={() => setReassignMenuId(reassignMenuId === item.id ? null : item.id)} disabled={isProcessing} />
                          {reassignMenuId === item.id && (
                            <div style={{
                              position: 'absolute', top: '100%', right: 0, zIndex: 50,
                              background: theme.colors.card, border: `1px solid ${theme.colors.border}`,
                              borderRadius: 8, boxShadow: theme.shadow.md, minWidth: 180, padding: '4px 0',
                              marginTop: 4, maxHeight: 200, overflowY: 'auto',
                            }}>
                              {agents.length === 0 && (
                                <div style={{ padding: '8px 14px', fontSize: 12, color: theme.colors.textLight }}>No agents found</div>
                              )}
                              {agents.map(a => (
                                <button key={a.id} onClick={() => processAction(item.id, 'reassign', { reassign_to: a.id, note: 'Assigned by broker' })} style={{
                                  display: 'block', width: '100%', padding: '8px 14px',
                                  background: 'transparent', border: 'none', cursor: 'pointer',
                                  fontSize: 12, color: theme.colors.text, textAlign: 'left',
                                }}
                                  onMouseEnter={e => { e.target.style.background = theme.colors.bg; }}
                                  onMouseLeave={e => { e.target.style.background = 'transparent'; }}
                                >
                                  {a.full_name || a.id}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                    );
                  })()}
                  {statusFilter === 'snoozed' && (
                    <span style={{ fontSize: 11, color: theme.colors.warning }}>
                      <Clock size={11} /> Returns {item.snooze_until ? new Date(item.snooze_until).toLocaleString() : 'soon'}
                    </span>
                  )}
                  {statusFilter === 'all' && item.status !== 'pending' && (
                    <span style={{
                      display: 'inline-block', padding: '2px 8px', borderRadius: 6,
                      background: theme.colors.bg, fontSize: 10, fontWeight: 600, color: theme.colors.textLight,
                      textTransform: 'capitalize',
                    }}>
                      {item.status}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function InboxActionBtn({ icon: Icon, label, color, onClick, disabled, done }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={label}
      style={{
        display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 1,
        padding: '4px 7px', border: done ? `1px solid ${color}` : 'none', borderRadius: 6,
        background: done ? `${color}18` : `${color}10`, color,
        fontSize: 9, fontWeight: done ? 700 : 600, cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        transition: 'background 0.15s, border 0.15s',
      }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.background = `${color}22`; }}
      onMouseLeave={e => { e.currentTarget.style.background = done ? `${color}18` : `${color}10`; }}
    >
      <Icon size={13} />
      {label}
    </button>
  );
}

function batchBtn(t) {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '5px 10px', border: 'none', borderRadius: 6,
    background: t.colors.primary, color: 'white',
    fontSize: 11, fontWeight: 600, cursor: 'pointer',
  };
}

// ──────────────────────────────────────────────────────────────────
// Smart Lists section (full tab content)
// ──────────────────────────────────────────────────────────────────
function SmartListsSection({ lists, loading, activeId, onSelect, onClear, onCreateNew, onEdit, onDelete,
  smartListContacts, smartListLoading, onOpenLead, selected, toggleSelect }) {

  // If a smart list is selected, show its contacts
  if (activeId) {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <button onClick={onClear} style={{
            padding: '5px 12px', borderRadius: 8, border: `1px solid ${theme.colors.border}`,
            background: theme.colors.card, cursor: 'pointer', fontSize: 12, fontWeight: 600,
            color: theme.colors.textLight, display: 'inline-flex', alignItems: 'center', gap: 4,
          }}>
            <ChevronRight size={12} style={{ transform: 'rotate(180deg)' }} /> Back to all segments
          </button>
          <span style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text }}>
            {lists.find(l => l.id === activeId)?.name}
          </span>
          <span style={{ fontSize: 12, color: theme.colors.textLight }}>
            {smartListContacts.length} contact{smartListContacts.length !== 1 ? 's' : ''}
          </span>
        </div>
        {smartListLoading ? (
          <div style={{ padding: 40, textAlign: 'center', color: theme.colors.textLight, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Loading…
          </div>
        ) : smartListContacts.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: theme.colors.textLight }}>
            No contacts match this segment.
          </div>
        ) : (
          <SmartListTable rows={smartListContacts} onOpen={onOpenLead} selected={selected} toggleSelect={toggleSelect} />
        )}
      </div>
    );
  }

  // Smart list directory — show all lists as clickable cards
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <p style={{ fontSize: 13, color: theme.colors.textLight, margin: 0 }}>
          Click a segment to view matching contacts.
        </p>
        <button onClick={onCreateNew} style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          padding: '8px 14px', borderRadius: 10, border: 'none',
          background: theme.colors.primary, color: 'white',
          fontSize: 12, fontWeight: 700, cursor: 'pointer',
        }}>
          <Plus size={14} /> New Segment
        </button>
      </div>

      {loading && (
        <div style={{ padding: 40, textAlign: 'center', color: theme.colors.textLight }}>
          <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Loading…
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
        {lists.map(sl => (
          <div key={sl.id} onClick={() => onSelect(sl.id)} style={{
            background: theme.colors.card, border: `1px solid ${theme.colors.border}`,
            borderLeft: `4px solid ${sl.color || theme.colors.primary}`,
            borderRadius: 10, padding: '14px 16px', cursor: 'pointer',
            boxShadow: theme.shadow.sm, transition: 'box-shadow 0.15s',
          }}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = theme.shadow.md; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = theme.shadow.sm; }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text, marginBottom: 4 }}>{sl.name}</div>
                <div style={{ fontSize: 11, color: theme.colors.textLight }}>
                  {sl.cached_count != null ? `${sl.cached_count} contacts` : '—'}
                  {sl.is_system && <span style={{ marginLeft: 6, color: theme.colors.primary, fontWeight: 600 }}>System</span>}
                  {sl.is_shared && !sl.is_system && <span style={{ marginLeft: 6 }}>· Shared</span>}
                </div>
              </div>
              {!sl.is_system && (
                <div style={{ display: 'flex', gap: 4 }}>
                  <button onClick={e => { e.stopPropagation(); onEdit(sl); }} title="Edit" style={slCardBtn}>
                    <Edit2 size={12} />
                  </button>
                  <button onClick={e => { e.stopPropagation(); onDelete(sl.id); }} title="Delete" style={slCardBtn}>
                    <Trash2 size={12} />
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const slCardBtn = {
  background: 'none', border: 'none', cursor: 'pointer', padding: 4,
  color: theme.colors.textLight, borderRadius: 4,
};

// ──────────────────────────────────────────────────────────────────
// Coming Soon placeholder
// ──────────────────────────────────────────────────────────────────
function ComingSoon({ label, desc }) {
  return (
    <div style={{
      padding: '60px 20px', textAlign: 'center',
    }}>
      <div style={{
        width: 56, height: 56, borderRadius: 28, background: `${theme.colors.primary}14`,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16,
      }}>
        <Filter size={24} color={theme.colors.primary} />
      </div>
      <h3 style={{ fontSize: 18, fontWeight: 700, color: theme.colors.text, margin: '0 0 8px' }}>{label}</h3>
      <p style={{ fontSize: 13, color: theme.colors.textLight, maxWidth: 420, margin: '0 auto', lineHeight: 1.5 }}>
        {desc}
      </p>
      <div style={{
        display: 'inline-block', marginTop: 16, padding: '6px 14px',
        borderRadius: 8, background: theme.colors.bg, border: `1px solid ${theme.colors.border}`,
        fontSize: 12, fontWeight: 600, color: theme.colors.primary,
      }}>
        Coming Soon
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Smart List Editor Modal (create/edit filters)
// ──────────────────────────────────────────────────────────────────
const SL_STATUS_OPTIONS = ['new', 'contacted', 'qualified', 'nurturing', 'under_contract', 'closed', 'lost'];
const SL_TYPE_OPTIONS = ['buyer', 'seller', 'renter', 'commercial', 'pm_owner', 'investor'];
const SL_SORT_OPTIONS = [
  { value: 'lead_score_desc', label: 'Score (high → low)' },
  { value: 'lead_score_asc', label: 'Score (low → high)' },
  { value: 'last_contacted_asc', label: 'Last contacted (oldest)' },
  { value: 'last_contacted_desc', label: 'Last contacted (newest)' },
  { value: 'created_at_desc', label: 'Newest first' },
  { value: 'created_at_asc', label: 'Oldest first' },
];
const SL_COLORS = ['#EF4444', '#F97316', '#EAB308', '#22C55E', '#3B82F6', '#8B5CF6', '#EC4899', '#6B7280'];

function SmartListEditorModal({ smartList, onSave, onClose, agents }) {
  const [name, setName] = useState(smartList?.name || '');
  const [color, setColor] = useState(smartList?.color || '#3B82F6');
  const [filter, setFilter] = useState(smartList?.filter || {});
  const [sortBy, setSortBy] = useState(smartList?.sort_by || 'lead_score_desc');
  const [isShared, setIsShared] = useState(smartList?.is_shared !== false);

  const updateFilter = (key, val) => {
    setFilter(prev => {
      const next = { ...prev };
      if (val === '' || val === null || val === undefined || (Array.isArray(val) && !val.length)) delete next[key];
      else next[key] = val;
      return next;
    });
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 999,
      background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={onClose}>
      <div style={{
        background: 'white', borderRadius: 16, width: 520, maxHeight: '85vh',
        overflow: 'auto', padding: 24, boxShadow: theme.shadow.lg,
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: theme.colors.text }}>
            {smartList ? 'Edit Segment' : 'New Segment'}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <X size={18} color={theme.colors.textLight} />
          </button>
        </div>

        {/* Name */}
        <label style={slLabel}>Name</label>
        <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g., Hot Buyers > $500K"
          style={slInput} />

        {/* Color */}
        <label style={{ ...slLabel, marginTop: 14 }}>Color</label>
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          {SL_COLORS.map(c => (
            <button key={c} onClick={() => setColor(c)} style={{
              width: 26, height: 26, borderRadius: 13, background: c,
              border: color === c ? '3px solid ' + theme.colors.text : '2px solid transparent',
              cursor: 'pointer',
            }} />
          ))}
        </div>

        {/* Filters */}
        <div style={{ background: theme.colors.bg, borderRadius: 12, padding: 14, marginBottom: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: theme.colors.text, marginBottom: 10 }}>Filters</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <label style={slFilterLabel}>Status</label>
              <select multiple value={filter.lead_status || []} onChange={e => {
                updateFilter('lead_status', Array.from(e.target.selectedOptions, o => o.value));
              }} style={{ ...slInput, height: 80 }}>
                {SL_STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
              </select>
            </div>
            <div>
              <label style={slFilterLabel}>Type</label>
              <select multiple value={filter.lead_type || []} onChange={e => {
                updateFilter('lead_type', Array.from(e.target.selectedOptions, o => o.value));
              }} style={{ ...slInput, height: 80 }}>
                {SL_TYPE_OPTIONS.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
              </select>
            </div>
            <div>
              <label style={slFilterLabel}>Min Score</label>
              <input type="number" min={0} max={100} value={filter.lead_score_min || ''} placeholder="0"
                onChange={e => updateFilter('lead_score_min', e.target.value ? parseInt(e.target.value) : null)}
                style={slInput} />
            </div>
            <div>
              <label style={slFilterLabel}>No Contact (days)</label>
              <input type="number" min={1} value={filter.stale_days_gt || ''} placeholder="e.g., 14"
                onChange={e => updateFilter('stale_days_gt', e.target.value ? parseInt(e.target.value) : null)}
                style={slInput} />
            </div>
            <div>
              <label style={slFilterLabel}>Assigned To</label>
              <select value={filter.assigned_to || ''} onChange={e => updateFilter('assigned_to', e.target.value || null)} style={slInput}>
                <option value="">Anyone</option>
                <option value="{{current_user}}">Me</option>
                <option value="unassigned">Unassigned</option>
                {(agents || []).map(a => <option key={a.id} value={a.id}>{a.full_name}</option>)}
              </select>
            </div>
            <div>
              <label style={slFilterLabel}>City</label>
              <input value={filter.city || ''} placeholder="e.g., Medford"
                onChange={e => updateFilter('city', e.target.value || null)} style={slInput} />
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 12, color: theme.colors.textLight, cursor: 'pointer' }}>
            <input type="checkbox" checked={filter.next_follow_up_before === '{{today}}'}
              onChange={e => updateFilter('next_follow_up_before', e.target.checked ? '{{today}}' : null)} />
            Follow-up overdue
          </label>
        </div>

        {/* Sort */}
        <label style={slLabel}>Sort By</label>
        <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ ...slInput, marginBottom: 14 }}>
          {SL_SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>

        {/* Shared */}
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: theme.colors.textLight, marginBottom: 18, cursor: 'pointer' }}>
          <input type="checkbox" checked={isShared} onChange={e => setIsShared(e.target.checked)} />
          Share with team
        </label>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{
            padding: '8px 16px', borderRadius: 8, border: `1px solid ${theme.colors.border}`,
            background: 'white', cursor: 'pointer', fontSize: 13,
          }}>Cancel</button>
          <button onClick={() => {
            if (!name.trim()) return;
            onSave({ name: name.trim(), color, filter, sort_by: sortBy, is_shared: isShared });
          }} style={{
            padding: '8px 16px', borderRadius: 8, border: 'none',
            background: theme.colors.primary, color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600,
          }}>
            {smartList ? 'Update' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

const slLabel = { display: 'block', fontSize: 12, fontWeight: 600, color: theme.colors.text, marginBottom: 4 };
const slFilterLabel = { display: 'block', fontSize: 11, fontWeight: 500, color: theme.colors.textLight, marginBottom: 3 };
const slInput = {
  width: '100%', padding: '7px 10px', borderRadius: 8, border: `1px solid ${theme.colors.border}`,
  fontSize: 13, outline: 'none', boxSizing: 'border-box',
};

// ──────────────────────────────────────────────────────────────────
// Smart List — Table view (detailed rows with columns)
// ──────────────────────────────────────────────────────────────────
function SmartListTable({ rows, onOpen, selected, toggleSelect }) {
  return (
    <div style={{ background: theme.colors.card, border: `1px solid ${theme.colors.border}`, borderRadius: 12, overflow: 'hidden' }}>
      {/* Table header */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '36px 1fr 120px 100px 90px 90px 100px 80px',
        gap: 0, padding: '10px 14px',
        borderBottom: `1px solid ${theme.colors.border}`,
        background: '#F8FAFC',
      }}>
        {['', 'Contact', 'Status', 'Type', 'Score', 'Stale', 'Source', 'Agent'].map((h, i) => (
          <div key={i} style={{ fontSize: 10, fontWeight: 700, color: theme.colors.textLight, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</div>
        ))}
      </div>
      {/* Rows */}
      {rows.map((l, i) => {
        const statusMeta = PIPELINE.find(p => p.key === l.lead_status) || { color: theme.colors.textLight, label: l.lead_status };
        const initials = `${l.first_name?.[0] || ''}${l.last_name?.[0] || ''}`.toUpperCase();
        return (
          <div key={l.id} onClick={() => onOpen(l.id)} style={{
            display: 'grid',
            gridTemplateColumns: '36px 1fr 120px 100px 90px 90px 100px 80px',
            gap: 0, padding: '10px 14px', cursor: 'pointer',
            borderBottom: i < rows.length - 1 ? `1px solid ${theme.colors.border}` : 'none',
            background: selected.has(l.id) ? `${theme.colors.primary}08` : 'transparent',
            transition: 'background 0.1s',
          }}
            onMouseEnter={e => { if (!selected.has(l.id)) e.currentTarget.style.background = '#FAFBFC'; }}
            onMouseLeave={e => { if (!selected.has(l.id)) e.currentTarget.style.background = 'transparent'; }}
          >
            {/* Checkbox */}
            <div>
              <button onClick={e => { e.stopPropagation(); toggleSelect(l.id); }} style={{
                background: 'transparent', border: 'none', cursor: 'pointer', padding: 0,
                color: selected.has(l.id) ? theme.colors.primary : theme.colors.textLight,
              }}>
                {selected.has(l.id) ? <CheckSquare size={15} /> : <Square size={15} />}
              </button>
            </div>
            {/* Contact */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <div style={{
                width: 28, height: 28, borderRadius: 14, flexShrink: 0,
                background: theme.colors.primary, color: 'white',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 10, fontWeight: 700,
              }}>{initials}</div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: theme.colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {l.first_name} {l.last_name}
                  {l.pinned && <Star size={10} fill={theme.colors.accent} color={theme.colors.accent} style={{ marginLeft: 4 }} />}
                </div>
                <div style={{ fontSize: 11, color: theme.colors.textLight, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {l.email || l.phone || l.city || '—'}
                </div>
              </div>
            </div>
            {/* Status */}
            <div>
              <span style={{
                display: 'inline-block', padding: '2px 8px', borderRadius: 6,
                background: `${statusMeta.color}18`, color: statusMeta.color,
                fontSize: 11, fontWeight: 600, textTransform: 'capitalize',
              }}>{(l.lead_status || '').replace(/_/g, ' ')}</span>
            </div>
            {/* Type */}
            <div style={{ fontSize: 12, color: theme.colors.text, textTransform: 'capitalize' }}>
              {l.lead_type || '—'}
            </div>
            {/* Score */}
            <div>{l._score != null && <ScoreChip score={l._score} />}</div>
            {/* Staleness */}
            <div><StalenessPill staleness={l._staleness} days={l._days_since} /></div>
            {/* Source */}
            <div style={{ fontSize: 11, color: theme.colors.textLight, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {l.lead_source || '—'}
            </div>
            {/* Agent */}
            <div style={{ fontSize: 11, color: theme.colors.textLight, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {l.agent_name || '—'}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Smart List — Card grid view
// ──────────────────────────────────────────────────────────────────
function SmartListCards({ rows, onOpen, selected, toggleSelect }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
      gap: 12,
    }}>
      {rows.map(l => {
        const statusMeta = PIPELINE.find(p => p.key === l.lead_status) || { color: theme.colors.textLight, label: l.lead_status };
        const initials = `${l.first_name?.[0] || ''}${l.last_name?.[0] || ''}`.toUpperCase();
        return (
          <div key={l.id} onClick={() => onOpen(l.id)} style={{
            background: theme.colors.card,
            border: `1px solid ${selected.has(l.id) ? theme.colors.primary : theme.colors.border}`,
            borderLeft: `3px solid ${statusMeta.color}`,
            borderRadius: 12, padding: 14, cursor: 'pointer',
            boxShadow: theme.shadow.sm,
            transition: 'box-shadow 0.15s, border-color 0.15s',
          }}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = theme.shadow.md; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = theme.shadow.sm; }}
          >
            {/* Top row: checkbox + avatar + name + score */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <button onClick={e => { e.stopPropagation(); toggleSelect(l.id); }} style={{
                background: 'transparent', border: 'none', cursor: 'pointer', padding: 0,
                color: selected.has(l.id) ? theme.colors.primary : theme.colors.textLight,
              }}>
                {selected.has(l.id) ? <CheckSquare size={15} /> : <Square size={15} />}
              </button>
              <div style={{
                width: 30, height: 30, borderRadius: 15, flexShrink: 0,
                background: theme.colors.primary, color: 'white',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 700,
              }}>{initials}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: theme.colors.text, display: 'flex', alignItems: 'center', gap: 5 }}>
                  {l.first_name} {l.last_name}
                  {l.pinned && <Star size={10} fill={theme.colors.accent} color={theme.colors.accent} />}
                </div>
              </div>
              {l._score != null && <ScoreChip score={l._score} />}
            </div>

            {/* Details */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, fontSize: 11, color: theme.colors.textLight, marginBottom: 8 }}>
              <span style={{
                padding: '1px 7px', borderRadius: 6,
                background: `${statusMeta.color}18`, color: statusMeta.color,
                fontWeight: 600, textTransform: 'capitalize',
              }}>{(l.lead_status || '').replace(/_/g, ' ')}</span>
              {l.lead_type && <span style={{ textTransform: 'capitalize' }}>· {l.lead_type}</span>}
              {l.lead_source && <span>· {l.lead_source}</span>}
              <StalenessPill staleness={l._staleness} days={l._days_since} />
            </div>

            {/* Contact info */}
            <div style={{ fontSize: 11, color: theme.colors.textLight, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {l.email && <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.email}</div>}
              {l.phone && <div>{l.phone}</div>}
              {l.budget && <div>Budget: {l.budget}</div>}
            </div>

            {/* Tags */}
            {(l.tags || []).length > 0 && (
              <div style={{ display: 'flex', gap: 4, marginTop: 8, flexWrap: 'wrap' }}>
                {l.tags.slice(0, 4).map(t => (
                  <span key={t} style={{
                    padding: '1px 6px', borderRadius: 6,
                    background: `${theme.colors.primary}14`, color: theme.colors.primary,
                    fontSize: 10, fontWeight: 600,
                  }}>{t}</span>
                ))}
                {l.tags.length > 4 && (
                  <span style={{ fontSize: 10, color: theme.colors.textLight }}>+{l.tags.length - 4}</span>
                )}
              </div>
            )}

            {/* Agent + quick actions */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, paddingTop: 8, borderTop: `1px solid ${theme.colors.border}` }}>
              <span style={{ fontSize: 10, color: theme.colors.textLight }}>{l.agent_name || 'Unassigned'}</span>
              <div style={{ display: 'flex', gap: 3 }}>
                {l.phone && <CardIcon href={`tel:${l.phone}`} icon={<Phone size={11} />} title="Call" />}
                {l.phone && <CardIcon href={`sms:${l.phone}`} icon={<MessageSquare size={11} />} title="Text" />}
                {l.email && <CardIcon href={`mailto:${l.email}`} icon={<Mail size={11} />} title="Email" />}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Kanban view
// ──────────────────────────────────────────────────────────────────
function Kanban({ byStatus, onOpen, onDrop, selected, toggleSelect, pipeline = PIPELINE }) {
  // setDraggingId is passed down as a side-effect hook; draggingId value itself is unused at this level
  const setDraggingId = useState(null)[1];
  return (
    <div data-tour="kanban-board" style={{
      display: 'grid',
      gridTemplateColumns: `repeat(${pipeline.length}, minmax(210px, 1fr))`,
      gap: 10, paddingBottom: 8,
      // NOTE: no overflow-x here on purpose — any `overflow` value on an
      // ancestor creates a scroll container that breaks `position: sticky`
      // on the column headers. On narrow screens, the body scrolls
      // horizontally instead (acceptable for this view).
    }}>
      {pipeline.map(stage => (
        <KanbanColumn
          key={stage.key}
          stage={stage}
          leads={byStatus[stage.key] || []}
          onOpen={onOpen}
          onDrop={onDrop}
          setDraggingId={setDraggingId}
          selected={selected}
          toggleSelect={toggleSelect}
        />
      ))}
    </div>
  );
}

function KanbanColumn({ stage, leads, onOpen, onDrop, setDraggingId, selected, toggleSelect }) {
  const [over, setOver] = useState(false);
  return (
    <div
      onDragOver={e => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={e => {
        setOver(false);
        const data = e.dataTransfer.getData('text/plain');
        try {
          const lead = JSON.parse(data);
          onDrop(lead, stage.key);
        } catch { /* noop */ }
      }}
      style={{
        background: over ? '#EEF7F5' : '#F8FAFC',
        border: `1px solid ${theme.colors.border}`, borderRadius: 12,
        display: 'flex', flexDirection: 'column', minHeight: 200,
      }}
    >
      {/* Sticky column header — stays visible when scrolling a long column.
          `top` = TopBar (56) + Leads sticky header (~104) ≈ 160. Adjust if
          either grows. */}
      <div style={{
        padding: '10px 12px', borderBottom: `1px solid ${theme.colors.border}`,
        display: 'flex', alignItems: 'center', gap: 8,
        position: 'sticky', top: 160, zIndex: 10,
        background: over ? '#EEF7F5' : '#F8FAFC',
        borderTopLeftRadius: 12, borderTopRightRadius: 12,
      }}>
        <div style={{ width: 8, height: 8, borderRadius: 4, background: stage.color }} />
        <div style={{ fontSize: 12, fontWeight: 700, color: theme.colors.text }}>{stage.label}</div>
        <div style={{ marginLeft: 'auto', fontSize: 11, color: theme.colors.textLight }}>{leads.length}</div>
      </div>
      <div style={{ flex: 1, padding: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {leads.map(l => (
          <LeadCard
            key={l.id}
            lead={l}
            onOpen={() => onOpen(l.id)}
            onDragStart={() => setDraggingId(l.id)}
            onDragEnd={() => setDraggingId(null)}
            draggable
            selected={selected.has(l.id)}
            onToggleSelect={(e) => { e.stopPropagation(); toggleSelect(l.id); }}
          />
        ))}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// List view
// ──────────────────────────────────────────────────────────────────
function ListTable({ rows, onOpen, selected, toggleSelect }) {
  return (
    <div style={{ background: theme.colors.card, border: `1px solid ${theme.colors.border}`, borderRadius: 12, overflow: 'hidden' }}>
      {rows.map((l, i) => (
        <LeadCard
          key={l.id}
          lead={l}
          onOpen={() => onOpen(l.id)}
          divider={i < rows.length - 1}
          selected={selected.has(l.id)}
          onToggleSelect={(e) => { e.stopPropagation(); toggleSelect(l.id); }}
          listMode
        />
      ))}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Lead Card (shared by both views)
// ──────────────────────────────────────────────────────────────────
function LeadCard({ lead, onOpen, draggable, onDragStart, onDragEnd, listMode, divider, selected, onToggleSelect }) {
  const initials = `${lead.first_name?.[0] || ''}${lead.last_name?.[0] || ''}`.toUpperCase();
  const statusMeta = PIPELINE.find(p => p.key === lead.lead_status) || { color: theme.colors.textLight, label: lead.lead_status };

  return (
    <div
      draggable={draggable}
      onDragStart={e => {
        e.dataTransfer.setData('text/plain', JSON.stringify(lead));
        onDragStart?.();
      }}
      onDragEnd={onDragEnd}
      onClick={onOpen}
      style={{
        background: theme.colors.card,
        border: listMode ? 'none' : `1px solid ${theme.colors.border}`,
        borderLeft: listMode ? `3px solid ${statusMeta.color}` : `3px solid ${statusMeta.color}`,
        borderBottom: listMode && divider ? `1px solid ${theme.colors.border}` : undefined,
        borderRadius: listMode ? 0 : 10,
        padding: '10px 12px', cursor: 'pointer',
        boxShadow: listMode ? 'none' : '0 1px 3px rgba(0,0,0,0.04)',
        display: 'flex', gap: 10, alignItems: listMode ? 'center' : 'flex-start',
      }}
    >
      <button
        onClick={onToggleSelect}
        style={{
          background: 'transparent', border: 'none', cursor: 'pointer',
          color: selected ? theme.colors.primary : theme.colors.textLight,
          padding: 0, flexShrink: 0,
        }}
        aria-label={selected ? 'Deselect' : 'Select'}
      >
        {selected ? <CheckSquare size={16} /> : <Square size={16} />}
      </button>
      <div style={{
        width: 30, height: 30, borderRadius: 15, flexShrink: 0,
        background: theme.colors.primary, color: 'white',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 11, fontWeight: 700,
      }}>{initials}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: theme.colors.text }}>{lead.first_name} {lead.last_name}</div>
          {lead.pinned && <Star size={11} fill={theme.colors.accent} color={theme.colors.accent} />}
          {lead._score != null && <ScoreChip score={lead._score} />}
          <StalenessPill staleness={lead._staleness} days={lead._days_since} />
          {(lead.contact_attempts || 0) > 0 && (
            <span title={`${lead.contact_attempts} of ${lead.max_contact_attempts || 5} contact attempts`} style={{
              display: 'inline-flex', alignItems: 'center', gap: 3,
              padding: '1px 6px', borderRadius: 8, fontSize: 10, fontWeight: 700,
              background: (lead.contact_attempts >= (lead.max_contact_attempts || 5))
                ? '#FEE2E2' : '#FEF3C7',
              color: (lead.contact_attempts >= (lead.max_contact_attempts || 5))
                ? '#DC2626' : '#D97706',
            }}>
              <Phone size={9} /> {lead.contact_attempts}/{lead.max_contact_attempts || 5}
            </span>
          )}
        </div>
        <div style={{ fontSize: 11, color: theme.colors.textLight, marginTop: 2, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {lead.lead_type && <span style={{ textTransform: 'capitalize' }}>{lead.lead_type}</span>}
          {lead.lead_source && <span>· {lead.lead_source}</span>}
          {lead.budget && <span>· {lead.budget}</span>}
          {lead.city && <span>· {lead.city}</span>}
        </div>
        {(lead.tags || []).length > 0 && (
          <div style={{ display: 'flex', gap: 4, marginTop: 6, flexWrap: 'wrap' }}>
            {lead.tags.map(t => (
              <span key={t} style={{
                padding: '1px 7px', borderRadius: 8,
                background: `${theme.colors.primary}14`, color: theme.colors.primary,
                fontSize: 10, fontWeight: 600,
              }}>{t}</span>
            ))}
          </div>
        )}
      </div>
      {listMode && (
        <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
          {lead.phone && <CardIcon href={`tel:${lead.phone}`} icon={<Phone size={12} />} title="Call" />}
          {lead.phone && <CardIcon href={`sms:${lead.phone}`} icon={<MessageSquare size={12} />} title="Text" />}
          {lead.email && <CardIcon href={`mailto:${lead.email}`} icon={<Mail size={12} />} title="Email" />}
          <ChevronRight size={14} color={theme.colors.textLight} style={{ marginLeft: 4, alignSelf: 'center' }} />
        </div>
      )}
    </div>
  );
}

function CardIcon({ href, icon, title }) {
  return (
    <a
      href={href}
      onClick={e => e.stopPropagation()}
      title={title}
      style={{
        width: 24, height: 24, borderRadius: 6,
        background: '#F1F5F9', color: theme.colors.textLight,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        textDecoration: 'none',
      }}
    >{icon}</a>
  );
}

// ──────────────────────────────────────────────────────────────────
// Bulk actions bar
// ──────────────────────────────────────────────────────────────────
function BulkBar({ count, onClear, onStatus, onTag, onDelete, existingTags }) {
  const [tagOpen, setTagOpen] = useState(false);
  const [newTag, setNewTag] = useState('');
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '10px 14px', marginBottom: 12,
      background: theme.colors.primaryDark, color: 'white',
      borderRadius: 10,
    }}>
      <Users size={14} />
      <div style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{count} selected</div>

      <select onChange={e => { if (e.target.value) onStatus(e.target.value); e.target.value = ''; }} style={{
        padding: '5px 8px', borderRadius: 6, border: 'none', fontSize: 11, fontWeight: 600,
        background: 'rgba(255,255,255,0.15)', color: 'white', cursor: 'pointer',
      }}>
        <option value="">Move to…</option>
        {PIPELINE.map(p => <option key={p.key} value={p.key} style={{ color: 'black' }}>{p.label}</option>)}
      </select>

      <div style={{ position: 'relative' }}>
        <button onClick={() => setTagOpen(v => !v)} style={{
          padding: '5px 10px', borderRadius: 6, border: 'none',
          background: 'rgba(255,255,255,0.15)', color: 'white',
          fontSize: 11, fontWeight: 600, cursor: 'pointer',
        }}>+ Tag</button>
        {tagOpen && (
          <div style={{
            position: 'absolute', top: '100%', right: 0, marginTop: 4, zIndex: 10,
            background: theme.colors.card, border: `1px solid ${theme.colors.border}`,
            borderRadius: 8, padding: 10, minWidth: 220, boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          }}>
            <input autoFocus value={newTag} onChange={e => setNewTag(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { onTag(newTag.trim()); setNewTag(''); setTagOpen(false); } }}
              placeholder="Type a tag and hit Enter"
              style={{ width: '100%', boxSizing: 'border-box', padding: '6px 8px', fontSize: 12, borderRadius: 6, border: `1px solid ${theme.colors.border}`, color: theme.colors.text }}
            />
            {existingTags?.length > 0 && (
              <div style={{ fontSize: 10, color: theme.colors.textLight, marginTop: 6 }}>
                Existing tags:
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                  {existingTags.slice(0, 8).map(t => (
                    <button key={t} onClick={() => { onTag(t); setTagOpen(false); }} style={{
                      padding: '2px 6px', fontSize: 10, fontWeight: 600, cursor: 'pointer',
                      borderRadius: 6, border: 'none', background: `${theme.colors.primary}14`, color: theme.colors.primary,
                    }}>{t}</button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <button onClick={onDelete} style={{
        padding: '5px 10px', borderRadius: 6, border: 'none',
        background: theme.colors.danger, color: 'white',
        fontSize: 11, fontWeight: 600, cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', gap: 4,
      }}>
        <Trash2 size={11} /> Delete
      </button>

      <button onClick={onClear} style={{
        padding: '5px 10px', borderRadius: 6, border: 'none',
        background: 'rgba(255,255,255,0.1)', color: 'white',
        fontSize: 11, fontWeight: 600, cursor: 'pointer',
      }}>Clear</button>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Chips + small helpers
// ──────────────────────────────────────────────────────────────────
function ScoreChip({ score }) {
  const color = score >= 75 ? theme.colors.success
              : score >= 50 ? theme.colors.warning
              : theme.colors.danger;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '0 6px', borderRadius: 6, minWidth: 22, height: 16,
      background: `${color}18`, color, fontSize: 10, fontWeight: 800,
      justifyContent: 'center',
    }}>{score}</span>
  );
}

function StalenessPill({ staleness, days }) {
  if (!staleness || staleness === 'fresh') return null;
  const meta = {
    warm: { color: theme.colors.info,    label: `${days ?? 0}d` },
    cool: { color: theme.colors.warning, label: `${days ?? 0}d` },
    cold: { color: theme.colors.danger,  label: `${days ?? '—'}d cold` },
  }[staleness];
  if (!meta) return null;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '1px 6px', borderRadius: 6,
      background: `${meta.color}18`, color: meta.color,
      fontSize: 10, fontWeight: 700,
    }}>{meta.label}</span>
  );
}

function SavedViewsPicker({ views, activeId, onApply, onSave, onDelete }) {
  const [open, setOpen] = useState(false);
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');
  const [makeDefault, setMakeDefault] = useState(false);
  const ref = useRef(null);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const active = views.find(v => v.id === activeId);
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(v => !v)} style={{
        padding: '9px 12px', borderRadius: 10,
        border: `1px solid ${theme.colors.border}`, background: theme.colors.card,
        cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5,
        fontSize: 12, fontWeight: 600, color: theme.colors.text,
      }}>
        <Bookmark size={13} color={active ? theme.colors.primary : theme.colors.textLight} />
        {active ? active.name : 'Views'}
        <ChevronDown size={12} />
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, marginTop: 4, zIndex: 20,
          background: theme.colors.card, border: `1px solid ${theme.colors.border}`,
          borderRadius: 10, minWidth: 240, boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          padding: 6,
        }}>
          {views.length === 0 && (
            <div style={{ padding: 10, fontSize: 11, color: theme.colors.textLight }}>
              No saved views yet.
            </div>
          )}
          {views.map(v => (
            <div key={v.id} style={{ display: 'flex', alignItems: 'center' }}>
              <button onClick={() => { onApply(v); setOpen(false); }} style={{
                flex: 1, textAlign: 'left', padding: '7px 10px', borderRadius: 6,
                background: v.id === activeId ? '#F0FDFA' : 'transparent',
                border: 'none', cursor: 'pointer',
                fontSize: 12, fontWeight: 600, color: theme.colors.text,
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                {v.is_default ? <BookmarkCheck size={12} color={theme.colors.accent} /> : <Bookmark size={12} color={theme.colors.textLight} />}
                {v.name}
              </button>
              <button onClick={() => onDelete(v.id)} title="Delete view" style={{
                padding: 6, border: 'none', background: 'transparent', cursor: 'pointer',
                color: theme.colors.textLight,
              }}><Trash2 size={11} /></button>
            </div>
          ))}
          <div style={{ borderTop: `1px solid ${theme.colors.border}`, marginTop: 6, paddingTop: 6 }}>
            {naming ? (
              <div style={{ padding: 8 }}>
                <input autoFocus value={name} onChange={e => setName(e.target.value)}
                  placeholder="View name (e.g. 'My hot buyers')"
                  onKeyDown={e => {
                    if (e.key === 'Enter' && name.trim()) {
                      onSave(name.trim(), makeDefault);
                      setName(''); setMakeDefault(false); setNaming(false); setOpen(false);
                    }
                    if (e.key === 'Escape') { setNaming(false); setName(''); }
                  }}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '6px 8px', fontSize: 12, borderRadius: 6, border: `1px solid ${theme.colors.border}`, color: theme.colors.text }}
                />
                <label style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 6, fontSize: 11, color: theme.colors.textLight }}>
                  <input type="checkbox" checked={makeDefault} onChange={e => setMakeDefault(e.target.checked)} />
                  Make this my default view
                </label>
                <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                  <button onClick={() => {
                    if (name.trim()) { onSave(name.trim(), makeDefault); setName(''); setMakeDefault(false); setNaming(false); setOpen(false); }
                  }} style={{ flex: 1, padding: '5px 8px', background: theme.colors.primary, color: 'white', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Save</button>
                  <button onClick={() => { setNaming(false); setName(''); }} style={{ padding: '5px 8px', background: 'transparent', color: theme.colors.textLight, border: `1px solid ${theme.colors.border}`, borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                </div>
              </div>
            ) : (
              <button onClick={() => setNaming(true)} style={{
                width: '100%', padding: '7px 10px', textAlign: 'left', borderRadius: 6,
                background: 'transparent', border: 'none', cursor: 'pointer',
                fontSize: 12, color: theme.colors.primary, fontWeight: 600,
                display: 'inline-flex', alignItems: 'center', gap: 5,
              }}>+ Save current filters as a view</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function FilterSelect({ value, onChange, options }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} style={{
      padding: '9px 10px', borderRadius: 10, border: `1px solid ${theme.colors.border}`,
      background: theme.colors.card, fontSize: 12, color: theme.colors.text, cursor: 'pointer',
    }}>
      {options.map(([v, l]) => <option key={v || '_all'} value={v}>{l}</option>)}
    </select>
  );
}

function primaryBtn() {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '8px 14px', borderRadius: 10, border: 'none',
    background: theme.colors.primary, color: 'white',
    fontSize: 12, fontWeight: 700, cursor: 'pointer',
  };
}
function ghostBtn() {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '8px 12px', borderRadius: 10,
    border: `1px solid ${theme.colors.border}`, background: 'transparent',
    color: theme.colors.text, fontSize: 12, fontWeight: 600, cursor: 'pointer',
  };
}
